<template>
  <div class="card">
    <div class="row p-2">
      <div class="col-12 col-lg-4" v-show="contactProfile && contactProfile.full_name">
        <div class="d-flex justify-content-start">
          <div class="text-white d-flex justify-content-center align-items-center rounded-circle"
               style="width: 60px; height: 60px;">
            <img width="60" height="60" :src="contactProfile.photo" alt="">
          </div>
          <div class="ms-3">
            <h5 class="mb-0">{{ contactProfile.full_name }}</h5>
            <div class="pt-2 profile-info">
              <p><strong>Department:</strong> {{ contactProfile.department }}</p>
              <p><strong>Designation:</strong> {{ contactProfile.designation }}</p>
              <p><strong>ID:</strong> {{ contactProfile.serial_no }}</p>
            </div>
          </div>
        </div>
      </div>

      <div class="col-12 col-lg-4" v-if="groupChartData">
        <div v-for="(score, i) in groupChartData" :key="i">
          <div class="rounded pb-1 text-center fw-semibold" >
            {{ score.name }}: {{ score.score }} / {{ score.weight }}
          </div>
        </div>
      </div>

      <div class="col-12 col-lg-4" v-if="groupChartData">
        <PieChart :chartData="getGroupChartData(groupChartData)" :width="300" :height="250"/>
      </div>

    </div>
  </div>

  <div class="card" v-for="(component, i) in componentList" :key="i">
    <div class="row p-2">

      <div class="bg-light border-bottom p-1">
        <div class="d-flex justify-content-between align-items-center">
          <div>
            <h5 class="mb-0 fw-bold">{{ component.name }}</h5>
          </div>
          <div class="d-flex align-items-center gap-2">
            <button @click="handleProcessComponent(component)"
                    v-if="component.calculation_type == 'manual' && apprisalData.ongoing_status == 1 && component.performance_apprisal_master.length == 0"
                    class="btn btn-primary btn-sm"
                    :disabled="loading"
            >
              Start
            </button>

            <button
                @click="onClickOpenTargetModal(component)"
                v-if="component.calculation_type == 'target'"
                class="btn btn-primary btn-sm"
                :disabled="loading"
            >
              Target
            </button>

            <span class="fw-semibold"  v-if="component.calculation_type != 'target'">
                Weight: {{ component.weight }}
                Score: {{ component.weighted_score || 0 }}
              </span>
          </div>
        </div>
      </div>

      <div class="row p-2" v-if="component.calculation_type != 'target'">
        <div class="col-12 col-lg-8 mb-3 mb-lg-0" v-if="component.calculation_type == 'auto'">
          <TreeComponentItem
              v-for="item in component.active_child"
              :key="item.name"
              :parent="component"
              :node="item"
          />
        </div>
        <div class="col-12 col-lg-8 mb-3 mb-lg-0" v-if="component.calculation_type == 'manual'">
          <PerformanceComponentForm
              v-for="item in component.active_child"
              :key="item.name"
              :parent="component.active_child"
              :node="item"
              :performanceAppraisalNote="performanceAppraisalNote"
              :performanceAppraisal="component.performance_apprisal"
          />
        </div>
        <div class="col-12 col-lg-4" v-if="component.calculation_type == 'manual' && component.performance_apprisal_master.length > 0">
          <PieChart :chartData="getChartData(component)" :width="350" :height="300" />
        </div>
      </div>

      <div class="row p-2" v-if="component.calculation_type == 'target'">
        <TargetAchievementTreeComponentForm
            v-for="item in component.active_child"
            :key="item.name"
            :parent="component"
            :isSupervisorAuthorized="true"
            :node="item"
        />
      </div>

      <div class="table-responsive" v-if="component.calculation_type == 'manual' && component.performance_apprisal_master.length > 0">
        <table class="table">
          <thead>
          <tr>
            <th>Reviewer Name & ID</th>
            <th>Reviewer Designation</th>
            <th>Status</th>
            <th>Completion Date</th>
            <th class="text-center">Score</th>
          </tr>
          </thead>
          <tbody>
          <tr v-for="(reviewer, index) in component.performance_apprisal_master" :key="index">
            <td>{{ reviewer.supervisor_name }} ({{ reviewer.serial_no }})</td>
            <td>{{ reviewer.supervisor_designation }}</td>
            <td class="text-capitalize">
              {{ reviewer.status }}
            </td>
            <td>{{ reviewer.completion_date }}</td>
            <td class="text-center">{{ reviewer.score }}</td>
          </tr>
          </tbody>
        </table>
      </div>

    </div>
  </div>

  <div class="card">
    <div class="col-12 p-2">
      <div class="mb-2">
        <label for="note" class="form-label fw-bold">Notes:</label>
        <textarea v-model="formData.remarks" placeholder="Remarks" class="form-control" rows="2"
                  id="note"></textarea>
      </div>
    </div>
    <div class="mt-2 d-flex justify-content-start justify-content-sm-center px-2 gap-1 flex-wrap pb-2">
      <button :disabled="loading"
              @click="handleSubmit"
              class="btn btn-primary">
        Save
      </button>
    </div>
  </div>

  <TargetAchievementModal
      v-if="store.state.isModalOpenTwo"
      :targetComponent="targetComponent"
      :contactProfileId="contactProfileId"
      :isSupervisor="isSupervisor"
      :isList="isList"
      @getApprisalData="getApprisalData"
  />
</template>


<script setup>
import PieChart from '@/components/atom/chart/pieChart'
import PerformanceComponentForm from '@/components/molecule/company/hrm-payroll/performance-appraisal/PerformanceComponentForm.vue';
import TargetAchievementTreeComponentForm from '@/components/molecule/company/hrm-payroll/performance-appraisal/TargetAchievementTreeComponentForm.vue';
import TreeComponentItem from '@/components/molecule/company/hrm-payroll/performance-appraisal/TreeComponentItem.vue';
import TargetAchievementModal from '@/components/molecule/company/hrm-payroll/performance-appraisal/TargetAchievementModal.vue';
import {useRoute, useRouter} from "vue-router";
import {inject, onMounted, ref, watch} from "vue";
import { chartColorPalette } from '@/services/utils/global.js';
import {useStore} from "vuex";
import handleHrPayroll from "@/services/modules/hrPayroll/hrPayroll";
import handleHospital from '@/services/modules/hospital'

const emit = defineEmits(['getApprisalData'])
const props = defineProps({
  componentList: {
    type: Array,
    default: [],
    required: true,
  },
  groupChartData: {
    type: Array,
    default: []
  },
  contactProfile: {
    type: Object,
    default: {}
  },
  apprisalData: {
    type: Object,
    default: {}
  },
  isSupervisor: {
    type: Boolean,
    default: false
  },
  isList: {
    type: Boolean,
    default: false
  },
  contactProfileId:{
    default:null
  },
  getApprisalData: {
    type:Function
  }
})

const store = useStore();
const route = useRoute();
const router = useRouter();
const showError = inject('showError');
const showSuccess = inject('showSuccess');
const loading = ref(false)
let formData = ref({
  company_id: route.params.companyId,
  remarks:'',
  components: []
})
const targetComponent = ref({})
const performanceAppraisalNote = ref([]);
const {
  createPerformanceApprisalScore,
  createPerformanceApprisal
} = handleHrPayroll()
const { fetchNoteMasterList } = handleHospital()

const handleSubmit = () => {
  loading.value = true;
  const payload = []
  let canEvaluate = true;
  let targetAchievementData = []

  props.componentList && props.componentList.forEach(component => {
    if (component.calculation_type === 'manual' && component.performance_apprisal_master.length > 0) {
      const masterId = component.performance_apprisal.id
      const scores = extractScores(component)

      scores.forEach(score => {
        payload.push({
          performance_apprisal_master_id: masterId,
          performance_component_setup_id: score.id,
          performance_apprisal_note_master_id: score.performance_apprisal_note_master_id,
          score: score.score
        })
      })
    }

    if (component.calculation_type === 'target'){
      let achievement = extractComponent(component);
       targetAchievementData.push({
           data: achievement,
           performance_component_setup_id: component.id
       });
    }
  })

  let apprisalData = {};
  apprisalData.components = payload;
  apprisalData.achievement_data = targetAchievementData;
  apprisalData.company_id = route.params.companyId;
  apprisalData.performance_apprisal_setup_id = props.apprisalData.id;
  apprisalData.user_contact_profile_id = props.contactProfile.id;
  apprisalData.remarks = formData.value.remarks;
  apprisalData.can_evaluate = canEvaluate;
  apprisalData.is_supervisor = true;
  let query =  `?company_id=${route.params.companyId}`;
  createPerformanceApprisalScore(apprisalData, query)
      .then(res => {
        if (!res.status) {
          showError(res.message)
          return
        }
        emit('getApprisalData')
        showSuccess(res.message)
      })
      .catch(err => {
        console.log(err)
      }).finally(() => {
    loading.value = false
  })
}

const extractComponent = (node) => {
  const endpoints = []
  const traverse = (n) => {
    if (n.type === 'endpoint') {
      endpoints.push({
        performance_component_setup_id: n.id,
        qty: n.qty ? n.qty : null,
        value: n.value ? n.value : 0,
        remarks: n.remarks ? n.remarks : 0,
      })
    }
    if (n.active_child && n.active_child.length) {
      n.active_child.forEach(traverse)
    }
  }
  traverse(node)
  return endpoints
}

const extractScores = (node) => {
  const endpoints = []
  const traverse = (n) => {
    if (n.type === 'endpoint') {
      endpoints.push({
        id: n.id,
        performance_apprisal_note_master_id: n.performance_apprisal_note_master ? n.performance_apprisal_note_master.id : null,
        score: n.performance_apprisal_note_master ? n.performance_apprisal_note_master.score : 0,
      })
    }
    if (n.active_child && n.active_child.length) {
      n.active_child.forEach(traverse)
    }
  }
  traverse(node)
  return endpoints
}

function onClickOpenTargetModal(component) {
  targetComponent.value = component;
  store.state.isModalOpenTwo = true
}

const extractRemarksFromComponents = () => {
  const manualComponents = props.componentList.filter(
      component => component.calculation_type === 'manual' && component.performance_apprisal
  )

  if (manualComponents.length > 0) {
    const firstManualComponent = manualComponents[0]
    formData.value.remarks = firstManualComponent.performance_apprisal.remarks || ''
  }
}

const getChartData = (component) => {
  let endpoints = component.endpoints;
  return {
    labels: endpoints.map(s => s.name),
    datasets: [
      {
        data: endpoints.map(s => s.weighted_score),
        backgroundColor: endpoints.map((s, index) => chartColorPalette[index % chartColorPalette.length]),
        borderWidth: 1
      }
    ]
  }
}

const getGroupChartData = (chartData) => {
  return {
    labels: chartData.map(s => s.name),
    datasets: [
      {
        data: chartData.map(s => s.score),
        backgroundColor: chartData.map((s, index) => chartColorPalette[index % chartColorPalette.length]),
        borderWidth: 1
      }
    ]
  }
}

const handleProcessComponent = (component) => {
  if (component.calculation_type != "manual"){
    showError("This component cannot be started because it's not a manual type.");
    return
  }

  loading.value = true;
  let query =  `?company_id=${route.params.companyId}`;
  let performanceData = {}
  if (props.apprisalData) performanceData.performance_apprisal_setup_id = props.apprisalData.id;
  performanceData.performance_component_setup_id = component.id;
  performanceData.user_contact_profile_id = props.contactProfile.id;
  performanceData.is_list = (props.isList === true ? 1 : 0)
  createPerformanceApprisal(performanceData, query)
      .then(res => {
        if (!res.status) {
          showError(res.message)
          return
        }
        emit('getApprisalData')
        showSuccess(res.message)
      })
      .catch(err => {
        console.log(err)
      }).finally(() => {
    loading.value = false
  })
}

watch(() => props.componentList, () => {
  extractRemarksFromComponents()
}, { deep: true, immediate: true })

onMounted(() => {
  let noteMasterQuery = `?company_id=${route.params.companyId}&is_all=true&status=active&note_type=performance_apprisal_score`
  fetchNoteMasterList(noteMasterQuery).then(res => {
    if (res.data) {
      performanceAppraisalNote.value = res.data
    }
  });

})

</script>
<style scoped>

</style>